import React, {
  DetailedHTMLProps,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";

import "./Img.scss";

import classNames from "classnames";

import { tryConvertHeicToJpeg } from "src/utils/heicRecovery";

interface Props
  extends DetailedHTMLProps<
    ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  fallbackImageSrc: string;
  fallbackImageClassName?: string;
  className?: string;
  /**
   * TikTok cover/thumbnail URLs served via the feed endpoint (not the single-post one) can come back
   * as HEIC, which most non-Safari browsers can't render natively.
   * See https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
   * Opt-in only, so an onError on a genuinely dead link elsewhere doesn't pay for an extra fetch.
   */
  allowHeicRecovery?: boolean;
  /** Downscale a recovered HEIC->JPEG to this longest-side size - only applied if the source is bigger. */
  heicRecoveryMaxDimension?: number;
}

export const Img: React.FC<Props> = (props) => {
  const {
    src,
    fallbackImageSrc,
    fallbackImageClassName,
    className,
    allowHeicRecovery,
    heicRecoveryMaxDimension,
    ...rest
  } = props;
  const [SRC, setSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [initialHidden, setInitialHidden] = useState(true);
  const [triedHeicConversion, setTriedHeicConversion] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setSrc(src);
    setTriedHeicConversion(false);

    // Converted HEIC->JPEG blobs are held in memory via their object URL until revoked -
    // otherwise browsing many TikTok rows in Creator Search leaks memory across route changes.
    // This cleanup runs both when `src` changes and on unmount.
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  const handleLoad = () => {
    setInitialHidden(false);
  };

  const handleError = async () => {
    if (allowHeicRecovery && !triedHeicConversion && src) {
      setTriedHeicConversion(true);

      const convertedSrc = await tryConvertHeicToJpeg(src, {
        maxDimension: heicRecoveryMaxDimension,
      });

      if (convertedSrc) {
        objectUrlRef.current = convertedSrc;
        setSrc(convertedSrc);

        return;
      }
    }

    setSrc(fallbackImageSrc);
    setHasError(true);
    setInitialHidden(false);
  };

  return (
    <img
      src={SRC || ""}
      onLoad={handleLoad}
      onError={handleError}
      className={classNames("custom-img", className, {
        fallbackImageClassName: hasError,
        "custom-img--hidden": initialHidden,
      })}
      {...rest}
    />
  );
};

export default Image;
